import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { getWebroot } from '../utils/webroot';

export type Theme = 'light' | 'dark' | 'auto' | string;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'auto';
  });

  const [isDark, setIsDark] = useState(false);
  const currentThemeLinkRef = useRef<HTMLLinkElement | null>(null);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  }, []);

  const loadThemeCSS = useCallback((themeName: string) => {
    // Remove existing theme CSS
    if (currentThemeLinkRef.current) {
      document.head.removeChild(currentThemeLinkRef.current);
      currentThemeLinkRef.current = null;
    }

    // Only load CSS for custom themes (not light/dark as they use Tailwind defaults)
    if (themeName !== 'light' && themeName !== 'dark') {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      
      const webroot = getWebroot();
      link.href = `${webroot}/themes/${themeName}.css`;
      link.onload = () => {
        // Apply theme class after CSS loads
        document.documentElement.classList.remove('dark', 'light', 'custom-theme');
        document.documentElement.classList.add('custom-theme');
      };
      document.head.appendChild(link);
      currentThemeLinkRef.current = link;
    }
  }, []);

  useEffect(() => {
    const updateTheme = () => {
      let shouldBeDark = false;

      if (theme === 'dark') {
        shouldBeDark = true;
      } else if (theme === 'light') {
        shouldBeDark = false;
      } else if (theme === 'auto') {
        shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      } else {
        // Custom theme
        shouldBeDark = false; // Custom themes use light base by default
      }

      setIsDark(shouldBeDark);
      
      // Remove all theme classes first
      document.documentElement.classList.remove('dark', 'light', 'custom-theme');
      
      // Load appropriate theme
      if (theme === 'auto') {
        // For auto mode, use default Tailwind classes
        if (shouldBeDark) {
          document.documentElement.classList.add('dark');
        }
        // Remove any custom theme CSS
        if (currentThemeLinkRef.current) {
          document.head.removeChild(currentThemeLinkRef.current);
          currentThemeLinkRef.current = null;
        }
      } else if (theme === 'light' || theme === 'dark') {
        // For light/dark, use default Tailwind classes
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        }
        // Remove any custom theme CSS
        if (currentThemeLinkRef.current) {
          document.head.removeChild(currentThemeLinkRef.current);
          currentThemeLinkRef.current = null;
        }
      } else {
        // For custom themes, load the CSS file
        loadThemeCSS(theme);
      }
    };

    updateTheme();

    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => updateTheme();
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, loadThemeCSS]);

  const contextValue = useMemo(() => ({ theme, setTheme, isDark }), [theme, setTheme, isDark]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}